import { Alert, Box, CircularProgress, Grid, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
// Components
import { Formtitle, IperiaLayout } from '../../components'
// Hooks
import { useIperiaStatus } from '../../hooks/useIperiaStatus'
// Utils
import { IperiaFormDefaultState } from '../../utils/constants'
import { IperiaFormType } from '../../utils/sharedTypes'
import { getApiErrorMessage } from '../../utils/apiErrors'
import { canEditSessionStartDate, sessionStartDateError } from '../../utils/iperiaNavigation'

type FormationFields = 'session_start_date'

const VerificationFormation = () => {
  const navigate = useNavigate()
  const { global_id: globalId } = useParams<{ global_id: string }>()
  const { status, loading, error, updateSummary } = useIperiaStatus()
  const [form, setForm] = useState<IperiaFormType>(IperiaFormDefaultState)
  const [errors, setErrors] = useState<Partial<Record<FormationFields, string>>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!status?.learner_summary) return

    setForm({
      ...IperiaFormDefaultState,
      data: {
        ...IperiaFormDefaultState.data,
        ...status.learner_summary
      }
    })
  }, [status?.learner_summary])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !status) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        Impossible de charger votre dossier.
      </Typography>
    )
  }

  const alreadyConfirmed = status.learner_summary_confirmed
  const dateEditable = canEditSessionStartDate(status)
  const formDisabled = !dateEditable
  // Mêmes règles que le CRM (25 jours minimum, plafond, pas de démarrage du 10 au 31 décembre) : seules
  // les dates acceptées sont proposées, le serveur restant juge en dernier ressort.
  const bounds = status.session_start_bounds
  const isDisabledDate = (day: dayjs.Dayjs) => sessionStartDateError(day.format('YYYY-MM-DD'), bounds) !== null

  const handleChange = (key: FormationFields, value: string) => {
    setSubmitError(null)
    setErrors((prev) => {
      const next = { ...prev }
      delete next[key]

      if (key === 'session_start_date') {
        if (!value || !dayjs(value).isValid()) {
          next.session_start_date = 'Veuillez entrer une date de début de session valide'
        } else {
          const outOfBounds = sessionStartDateError(value, bounds)
          if (outOfBounds) next.session_start_date = outOfBounds
        }
      }
      return next
    })

    setForm((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        [key]: value,
      },
    }))
  }

  const validateForm = () => {
    const value = form.data.session_start_date
    let message: string | undefined
    if (!value || !dayjs(value).isValid()) {
      message = 'Veuillez entrer une date de début de session valide'
    } else {
      message = sessionStartDateError(value, bounds) ?? undefined
    }
    setErrors(message ? { session_start_date: message } : {})
    return !message
  }

  const handleNext = async () => {
    if (!globalId) return

    // Hors brouillon la date est figée : on ne la renvoie pas, donc rien à valider ni à sauvegarder.
    if (!dateEditable) {
      navigate(`/iperia/${globalId}/verification/recapitulatif`)
      return
    }
    if (!validateForm()) return

    setSubmitting(true)
    setSubmitError(null)
    try {
      // Save formation data to backend
      await updateSummary({
        session_start_date: form.data.session_start_date,
      })

      // Une date replanifiée après la confirmation renvoie directement à la signature, où le bulletin
      // est préparé de nouveau ; sinon, étape suivante du parcours.
      navigate(
        status.workflow_status === 'draft'
          ? `/iperia/${globalId}/verification/recapitulatif`
          : `/iperia/${globalId}/signature`,
      )
    } catch (err) {
      console.error('Failed to update formation:', err)
      setSubmitError(getApiErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  const handleBack = () => {
    if (!globalId) return
    navigate(`/iperia/${globalId}/verification/adresse`)
  }

  let productImage: string | undefined = undefined
  if (form.data.product_code) {
    productImage = `https://walter-resources.s3.eu-west-3.amazonaws.com/formations/${form.data.product_code.toLowerCase()}/img2/logotype_black.svg`
  }

  return (
    <IperiaLayout
      productName={form.data.product_name}
      handleBack={handleBack}
      handleNext={handleNext}
      submitting={submitting}
      submittingNext={submitting}
      alreadyConfirmed={alreadyConfirmed}
    >
      <Formtitle
        title="Étape 3 : Votre formation"
        subtitle="Veuillez vérifier les informations concernant votre formation."
      />
      {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}

      <Grid container spacing={4} sx={{ mb: 6 }} alignItems="center">
        {productImage && (
          <Grid item xs="auto">
            <Box
              component="img"
              src={productImage}
              alt=""
              sx={{ width: 40, height: 40, objectFit: 'contain', display: 'block' }}
              onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                event.currentTarget.style.display = 'none'
              }}
            />
          </Grid>
        )}
        <Grid item xs sx={{ minWidth: 0 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {form.data.product_name || 'Formation non spécifiée'}
          </Typography>
          {form.data.product_code && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Code : {form.data.product_code}
            </Typography>
          )}
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <DatePicker
            label="Date de début de session"
            disabled={formDisabled}
            format="DD/MM/YYYY"
            value={form.data.session_start_date ? dayjs(form.data.session_start_date) : null}
            minDate={bounds ? dayjs(bounds.min) : undefined}
            maxDate={bounds ? dayjs(bounds.max) : undefined}
            shouldDisableDate={bounds ? isDisabledDate : undefined}
            onChange={(value) => {
              if (!value) {
                handleChange('session_start_date', '')
              } else if (dayjs(value).isValid() && `${dayjs(value).year()}`.length === 4) {
                handleChange('session_start_date', dayjs(value).format('YYYY-MM-DD'))
              }
            }}
            slotProps={{
              textField: {
                id: 'session_start_date',
                required: true,
                fullWidth: true,
                // Libellé figé en haut : sur mobile, le libellé qui remonte au toucher passait sous le doigt
                // et avalait le tap, le calendrier ne s'ouvrait qu'au second essai. La date est validée au
                // choix et à l'envoi, plus à la perte de focus (le calendrier prend le focus en s'ouvrant).
                InputLabelProps: { shrink: true },
                error: Boolean(errors.session_start_date),
                helperText:
                  errors.session_start_date ||
                  (bounds
                    ? `Choisissez une date à partir du ${dayjs(bounds.min).format('DD/MM/YYYY')}`
                    : `Date à laquelle vous commencerez votre formation sous réserve d'une signature aujourd'hui`),
              },
            }}
          />
        </Grid>
      </Grid>
    </IperiaLayout>
  )
}

export default VerificationFormation
